import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "../../../components/Layout/Layout";
import ConfirmModal from "../../../components/Modals/ConfirmModal";
import DiscountHubService from "../../../services/DiscountHubService";
import { HollowDotsSpinner } from "react-epic-spinners";
import { SearchIcon } from "@heroicons/react/solid";
import {
  UploadIcon,
  DocumentDownloadIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import DatePicker from "react-date-picker";
import Paginate from "../../../utils/Paginate";
import { convert, formatDate } from "../../../utils/convertDate";
import fire from "../../../utils/Toastr";
import { useListLoading } from "../../../hooks/useListLoading";
import TableSkeleton from "../../../components/TableSkeleton";
import BulkUploadModal from "../../../components/discount-hub/BulkUploadModal";
import TemplateDownloadButton from "../../../components/discount-hub/TemplateDownloadButton";

const bulkUploadConfig = {
  title: "Bulk Upload Referral Codes",
  resourceLabel: "referral codes",
  templateBaseName: "referral-codes-template",
  columns: [
    {
      label: "Referral Code",
      key: "referralCode",
      required: true,
      text: true,
    },
    { label: "Referral Name", key: "referralName", required: true },
    {
      label: "Referral Amount",
      key: "referralAmount",
      required: true,
      type: "number",
    },
  ],
  sampleRow: {
    referralCode: "PTB001",
    referralName: "Becon Daniel",
    referralAmount: 500,
  },
  rejectedColumns: [
    { label: "Row", key: "rowNumber" },
    { label: "Referral Code", key: "referralCode" },
    { label: "Referral Name", key: "referralName" },
    { label: "Reason", key: "reason" },
  ],
};

export default function ReferralCodes() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [search, setSearch] = useState("");
  const [formValues, setFormValues] = useState({ from: null, to: null });
  const [fromm, setFromm] = useState("");
  const [too, setToo] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { isSearching, startSearch, endSearch } = useListLoading();
  const pageSize = 10;
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  const fetchRecords = async (page = 1, filters = {}) => {
    const searchTerm = filters.search ?? search;
    const from = filters.from ?? fromm;
    const to = filters.to ?? too;
    const inactive = filters.includeInactive ?? includeInactive;
    const isInitialLoad = page === 1 && !searchTerm && !from && !to && !inactive;
    if (isInitialLoad) {
      setLoading(true);
    } else {
      startSearch();
    }
    try {
      const response = await DiscountHubService.listReferralCodes(
        page,
        pageSize,
        searchTerm,
        from,
        to,
        inactive
      );
      if (response && response.data) {
        setRecords(response.data.result || []);
        setPagination({
          pageIndex: response.data.pageIndex,
          pageSize,
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
          hasPreviousPage: response.data.hasPreviousPage,
          hasNextPage: response.data.hasNextPage,
        });
      }
} catch (error) {
      console.error("Error fetching referral codes:", error);
      fire("error", "Failed to load referral codes");
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        endSearch();
      }
    }
  };

  useEffect(() => {
    fetchRecords(1);
  }, []);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
    fetchRecords(newPage);
  };

  const handleDateFilter = async (e) => {
    e.preventDefault();
    const from = formValues.from ? convert(formatDate(formValues.from)) : "";
    const to = formValues.to ? convert(formatDate(formValues.to)) : "";
    setFromm(from);
    setToo(to);
    fetchRecords(1, { from, to });
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchRecords(1, { search });
  };

  const handleIncludeInactiveChange = (e) => {
    const val = e.target.checked;
    setIncludeInactive(val);
    fetchRecords(1, { includeInactive: val });
  };

  const exportCSV = async () => {
    setExportLoading(true);
    try {
      const resp = await DiscountHubService.exportReferralCodes(
        search,
        fromm,
        too,
        includeInactive
      );
      if (resp && resp.data) {
        window.open(resp.data);
      } else {
        fire("error", resp?.message || "Export failed");
      }
    } catch (error) {
      console.error("Error exporting referral codes:", error);
      fire("error", "Failed to export referral codes");
    } finally {
      setExportLoading(false);
    }
  };

const openDeactivateConfirm = (record) => {
    setConfirmState({
      record,
      title: "Deactivate Referral Code",
      message: `Are you sure you want to deactivate referral code "${record.referralCode}"?`,
    });
  };

  const confirmDeactivate = async () => {
    if (!confirmState) return;
    const { record } = confirmState;
    setActionLoading((prev) => ({ ...prev, [record.id]: true }));
    try {
      const resp = await DiscountHubService.deactivateReferralCode(record.id);
      if (resp && resp.status !== false) {
        fire("success", resp.message || "Referral code deactivated successfully");
        fetchRecords(pagination.pageIndex);
      } else {
        fire("error", resp?.message || "Failed to deactivate referral code");
      }
    } catch (error) {
      console.error("Error deactivating referral code:", error);
      fire("error", "Failed to deactivate referral code");
    } finally {
      setActionLoading((prev) => ({ ...prev, [record.id]: false }));
      setConfirmState(null);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return convert(dateStr);
  };

  return (
    <Layout pageTag="discount-hub" current="/discount-hub/referral-codes">
      <React.Fragment>
        <main>
          <div className="max-w-7xl">
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  Referral Codes
                </h1>
                <p className="mt-2 text-sm text-gray-700">
                  Manage referral codes for discount hub
                </p>
              </div>
              <div className="mt-4 sm:mt-0 sm:ml-4 flex flex-wrap items-center gap-3">
                <Link
                  to="/discount-hub/referral-codes/create"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Referral Code
                </Link>
                <button
                  type="button"
                  onClick={() => setBulkOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <UploadIcon className="h-4 w-4 mr-2" />
                  Bulk Upload
                </button>
                <TemplateDownloadButton config={bulkUploadConfig} />
                <button
                  type="button"
                  onClick={exportCSV}
                  disabled={exportLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-green-100 border border-green-300 rounded-md"
                >
                  <DocumentDownloadIcon className="w-4 mr-1.5" />
                  {exportLoading ? "Exporting..." : "Export"}
                </button>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-4 mb-6">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <p className="text-sm leading-3 text-gray-500 mb-1">From</p>
                  <DatePicker
                    onChange={(e) => setFormValues({ ...formValues, from: e })}
                    value={formValues.from}
                  />
                </div>
                <div>
                  <p className="text-sm leading-3 text-gray-500 mb-1">To</p>
                  <DatePicker
                    onChange={(e) => setFormValues({ ...formValues, to: e })}
                    value={formValues.to}
                  />
                </div>
                <div className="relative">
                  <p className="text-sm leading-3 text-gray-500 mb-1">Search</p>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      type="text"
                      name="search"
                      id="search"
                      value={search}
                      onChange={handleSearch}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(e);
                        }
                      }}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="Search by name/code"
                    />
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <button
                    onClick={handleDateFilter}
                    className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      setFormValues({ from: null, to: null });
                      setFromm("");
                      setToo("");
                      setSearch("");
                      fetchRecords(1, { from: "", to: "", search: "" });
                    }}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    Clear
                  </button>
                </div>
                <div className="flex items-center ml-auto">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeInactive}
                      onChange={handleIncludeInactiveChange}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Include Inactive</span>
                  </label>
                </div>
              </div>
            </div>

<div className="mt-6 relative overflow-hidden ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white">
              <div className="min-w-full py-2 align-middle md:px-6 lg:px-8 bg-white">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <HollowDotsSpinner size={50} color="#4D627E" />
                  </div>
                ) : isSearching ? (
                  <TableSkeleton columns={7} rows={10} />
                ) : records.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No referral codes found
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        {[
                          "Referral Name",
                          "Referral Code",
                          "Referral Amount",
                          "Platform",
                          "Created On",
                          "Status",
                          "Actions",
                        ].map((header) => (
                          <th
                            key={header}
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {records.map((record) => (
                        <tr key={record.id}>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                            {record.referralName || "N/A"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                            {record.referralCode || "N/A"}
                          </td>
<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {record.referralAmount !== undefined &&
                            record.referralAmount !== null
                              ? record.referralAmount.toLocaleString()
                              : "0"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {record.platform || "N/A"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {formatDateTime(record.createdOn)}
                          </td>
<td className="whitespace-nowrap px-3 py-4 text-sm">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                              {record.isDeleted ? "Inactive" : "Active"}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <div className="flex items-center space-x-2">
<Link
                                to={`/discount-hub/referral-codes/edit?id=${record.id}`}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
                              >
                                <PencilIcon className="w-3 h-3 mr-1" />
                                Edit
                              </Link>
                              {!record.isDeleted && (
                                <button
                                  type="button"
                                  disabled={actionLoading[record.id]}
                                  onClick={() => openDeactivateConfirm(record)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-60"
                                >
                                  {actionLoading[record.id] ? (
                                    <HollowDotsSpinner size={10} color="#FFFFFF" />
                                  ) : (
                                    <TrashIcon className="w-3 h-3 mr-1" />
                                  )}
                                  Deactivate
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
              {!loading && records.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-200 sm:px-6 bg-white">
                  <Paginate
                    pageIndex={pagination.pageIndex}
                    totalPages={pagination.totalPages}
                    hasPreviousPage={pagination.hasPreviousPage}
                    hasNextPage={pagination.hasNextPage}
                    totalCount={pagination.totalCount}
                    setPageIndex={handlePageChange}
                    previousPage={handlePageChange.bind(
                      null,
                      pagination.pageIndex - 1
                    )}
                    nextPage={handlePageChange.bind(
                      null,
                      pagination.pageIndex + 1
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        </main>
      </React.Fragment>
      <ConfirmModal
        isOpen={confirmState !== null}
        onClose={() => setConfirmState(null)}
        onConfirm={confirmDeactivate}
        title={confirmState?.title || ""}
        message={confirmState?.message || ""}
        confirmText="Deactivate"
        cancelText="Cancel"
      />
      <BulkUploadModal
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title={bulkUploadConfig.title}
        config={bulkUploadConfig}
        onSubmit={(payload) =>
          DiscountHubService.bulkCreateReferralCodes(payload)
        }
        onSuccess={() => fetchRecords(1)}
      />
    </Layout>
  );
}
